// Simple notification system to replace alert() calls

let notificationContainer: HTMLDivElement | null = null;

// Initialize notification container
const initNotificationContainer = (): HTMLDivElement => {
  if (!notificationContainer) {
    notificationContainer = document.createElement('div');
    notificationContainer.id = 'notification-container';
    notificationContainer.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      z-index: 10000;
      display: flex;
      flex-direction: column;
      gap: 10px;
      pointer-events: none;
    `;
    document.body.appendChild(notificationContainer);
  }
  return notificationContainer;
};

// Show notification
export const showNotification = (
  message: string,
  type: string = 'info',
  duration: number = 5000
): void => {
  const container = initNotificationContainer();

  const notification = document.createElement('div');
  notification.style.cssText = `
    background: ${getBackgroundColor(type)};
    color: white;
    padding: 12px 20px;
    border-radius: 6px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    max-width: 350px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 14px;
    line-height: 1.4;
    white-space: pre-line;
    pointer-events: auto;
    opacity: 0;
    transform: translateX(100%);
    transition: all 0.3s ease;
  `;

  notification.textContent = message;
  container.appendChild(notification);

  // Animate in
  setTimeout(() => {
    notification.style.opacity = '1';
    notification.style.transform = 'translateX(0)';
  }, 10);

  // Animate out and remove
  setTimeout(() => {
    notification.style.opacity = '0';
    notification.style.transform = 'translateX(100%)';
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
    }, 300);
  }, duration);
};

// Get background color based on notification type
const getBackgroundColor = (type: string): string => {
  switch (type) {
    case 'success':
      return '#10b981'; // green
    case 'error':
      return '#ef4444'; // red
    case 'warning':
      return '#f59e0b'; // yellow
    case 'info':
    default:
      return '#3b82f6'; // blue
  }
};

// Convenience methods
export const showSuccess = (message: string): void =>
  showNotification(message, 'success');
export const showError = (message: string): void =>
  showNotification(message, 'error');
export const showWarning = (message: string): void =>
  showNotification(message, 'warning');
export const showInfo = (message: string): void =>
  showNotification(message, 'info');
